/**
 * Renders geometric shapes for Non-Verbal Reasoning games.
 * Minimal text - visual only for grades 3–5.
 * Shape format: { t: 'c'|'s'|'t'|'d', f: 0|1, r?: 0|90|180|270 }
 * t = type (circle, square, triangle, diamond), f = filled, r = rotation degrees
 */
import React from "react";
import { Box } from "@mui/material";

const S = 36;
const FILL = "#333";
const STROKE = "#333";

const Circle = ({ size, filled }) => (
    <Box
        sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            border: `2px solid ${STROKE}`,
            bgcolor: filled ? FILL : "transparent",
        }}
    />
);

const Square = ({ size, filled }) => (
    <Box
        sx={{
            width: size * 0.85,
            height: size * 0.85,
            border: `2px solid ${STROKE}`,
            bgcolor: filled ? FILL : "transparent",
        }}
    />
);

const Triangle = ({ size, filled }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" style={{ display: "block" }}>
        <polygon
            points="20,2 38,38 2,38"
            fill={filled ? FILL : "none"}
            stroke={STROKE}
            strokeWidth="2"
        />
    </svg>
);

const Diamond = ({ size, filled }) => (
    <Box
        sx={{
            width: size * 0.7,
            height: size * 0.7,
            border: `2px solid ${STROKE}`,
            bgcolor: filled ? FILL : "transparent",
            transform: "rotate(45deg)",
        }}
    />
);

export const ShapeBlock = ({ shape, size = S }) => {
    if (!shape) return null;
    const { t, f } = shape;
    const rot = shape.r || 0;
    const filled = f === 1;
    const s = size;

    const Inner = t === "c" ? <Circle size={s} filled={filled} /> :
        t === "s" ? <Square size={s} filled={filled} /> :
        t === "t" ? <Triangle size={s} filled={filled} /> :
        t === "d" ? <Diamond size={s} filled={filled} /> : null;

    return (
        <Box sx={{ transform: `rotate(${rot}deg)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {Inner}
        </Box>
    );
};

/** Renders a sequence of shapes in a row */
export const ShapeSequence = ({ shapes, size = S, gap = 1 }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: gap, flexWrap: "wrap" }}>
        {shapes.map((sh, i) => (
            <ShapeBlock key={i} shape={sh} size={size} />
        ))}
    </Box>
);

/** Renders a grid (e.g. 2x2 matrix) */
export const ShapeGrid = ({ shapes, cols = 2, size = S }) => (
    <Box
        sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, ${size + 8}px)`,
            gap: 1,
            justifyContent: "center",
        }}
    >
        {shapes.map((sh, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                {sh === null ? (
                    <Box sx={{ width: size, height: size, border: "2px dashed #999", borderRadius: 1 }} />
                ) : (
                    <ShapeBlock shape={sh} size={size} />
                )}
            </Box>
        ))}
    </Box>
);
